import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";

import isEmpty from "src/utils/helpers";
import TextInput from "src/components/UI/TextInput";
import FormSelect from "src/components/UI/FormSelect";
import Loader from "src/components/Loader";

import { hideModal } from "src/actions/modal.actions";
import { getModalData, getAdmin } from "src/selectors";
import { addCatalogDiscount, updateCatalogDiscount, getCatalogDiscount } from "src/actions/accountDetailsActions/billing.actions";
import { getGeneralLedgers, getGeneralLedgerSubAccounts, getSystemLocations } from "src/actions/admin.actions";

const toDateValue = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const toDateString = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isValidCalendarDate = (val) => !val || (val instanceof Date && !isNaN(val.getTime()) && val.getFullYear() <= 9999);

const booleanSlider = (id, label, name, values, handleChange, handleBlur, disabled = false) => (
  <span className="h-check ml-3" style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
    <label htmlFor={id}>{label}&nbsp;&nbsp;</label>
    <div className="form-check checkbox-slider checkbox-slider--b-flat">
      <label>
        <input
          id={id}
          type="checkbox"
          name={name}
          checked={values[name] || false}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <span>&nbsp;</span>
      </label>
    </div>
  </span>
);

const generalLedgersAsOptions = (generalLedgers) =>
  Array.isArray(generalLedgers)
    ? generalLedgers.map((gl) => ({ label: gl.accountNumber, value: gl.id }))
    : [];

const generalLedgerSubAccountsAsOptions = (subAccounts) =>
  Array.isArray(subAccounts)
    ? subAccounts.map((sa) => ({ label: sa.subAccountNumber, value: sa.id }))
    : [];

const systemLocationsAsOptions = (locations) =>
  Array.isArray(locations)
    ? locations.map((l) => ({ label: l.description, value: l.id }))
    : [];

const matchCurrentSelectedGeneralLedger = (subAccounts, selectedGlId) => {
  if (subAccounts && selectedGlId) {
    return subAccounts.filter((sa) => sa.generalLedgerId == selectedGlId);
  }
  return subAccounts;
};

const removeSelectedLocations = (locations, ignoreIndex, selectedGeneralLedgers) => {
  if (locations && !isEmpty(selectedGeneralLedgers)) {
    const usedIds = selectedGeneralLedgers
      .filter((_, i) => i !== ignoreIndex)
      .map((gl) => gl.glLocationId);
    return locations.filter((l) => !usedIds.includes(l.id));
  }
  return locations;
};

/**
 * Add/Edit a product catalog discount
 *
 * @param {any} modalProps.value - Discount object (omit for create)
 * @param {(item) => void} modalProps.onSuccess - callback on successful save
 */
class EditDiscountModal extends Component {
  state = {
    alertMessage: "",
    isLoading: false,
    discountDetail: null,
    selectedGeneralLedgers: [{ glLocationId: "", glSubId: "", glId: "", glDescription: "" }],
    generalLedgerSelected: false,
    generalLedgersToDelete: [],
  };

  componentDidMount() {
    this.props.getGeneralLedgers();
    this.props.getGeneralLedgerSubAccounts();
    this.props.getSystemLocations();

    const { value: discount } = this.props.modalProps;
    if (discount?.id) {
      this.setState({ isLoading: true });
      this.props.getCatalogDiscount(discount.id).then((response) => {
        const detail = response?.data || null;
        const selectedGeneralLedgers = detail?.selectedGeneralLedgers?.length
          ? detail.selectedGeneralLedgers
          : [{ glLocationId: "", glSubId: "", glId: "", glDescription: "" }];
        this.setState({
          isLoading: false,
          discountDetail: detail,
          selectedGeneralLedgers,
          generalLedgerSelected: !!detail?.selectedGeneralLedgers?.length,
        });
      });
    } else if (discount?.selectedGeneralLedgers?.length) {
      this.setState({
        selectedGeneralLedgers: discount.selectedGeneralLedgers,
        generalLedgerSelected: true,
      });
    }
  }

  formSchema = () =>
    Yup.object().shape({
      name: Yup.string().required("Required").test('no-trailing-space', 'Cannot end with a space', (v) => !v || !v.endsWith(' ')),
      description: Yup.string().max(255, "Max 255 characters").test('no-trailing-space', 'Cannot end with a space', (v) => !v || !v.endsWith(' ')),
      percentage: Yup.number().required("A value must be supplied").typeError("A value must be supplied").min(0, "Must be at least 0").max(100, "Must be 100 or less").test("decimal", "Max 2 decimal places", (v) => v == null || /^\d+(\.\d{1,2})?$/.test(String(v))),
      amount: Yup.number().required("The value must be 0 or greater.").typeError("The value must be 0 or greater.").min(0, "The value must be 0 or greater.").max(9999999999.99, "Max 10 digits before the decimal").test("decimal", "Max 2 decimal places", (v) => v == null || /^\d{1,10}(\.\d{1,2})?$/.test(String(v))),
      validDays: Yup.number().required("The value must be 0 or greater.").typeError("The value must be 0 or greater.").min(0, "The value must be 0 or greater.").max(999, "Max 3 digits").test("integer", "Max 3 digits", (v) => v == null || /^\d{1,3}?$/.test(String(v))),
      validMonths: Yup.number().required("The value must be 0 or greater.").typeError("The value must be 0 or greater.").min(0, "The value must be 0 or greater.").max(999, "Max 3 digits").test("integer", "Max 3 digits", (v) => v == null || /^\d{1,3}?$/.test(String(v))),
      startDate: Yup.mixed().nullable().test("valid-date", "Please enter a valid date", (v) => isValidCalendarDate(v)).test("start-before-end", "Start date must be before end date", function(startDate) {
        const { endDate } = this.parent;
        if (!startDate || !endDate) return true;
        return startDate < endDate;
      }),
      endDate: Yup.mixed().nullable().test("valid-date", "Please enter a valid date", (v) => isValidCalendarDate(v)).test("end-after-start", "End date must be after start date", function(endDate) {
        const { startDate } = this.parent;
        if (!startDate || !endDate) return true;
        return endDate > startDate;
      }),
      selectedGeneralLedgers: this.state.generalLedgerSelected
        ? Yup.array(Yup.object().shape({
            glLocationId: Yup.number().required("Required").typeError("Required"),
            glId: Yup.number().required("Required").typeError("Required"),
            glSubId: Yup.number().required("Required").typeError("Required"),
          })).min(1)
        : Yup.array(),
    });

  hideModal = () => {
    this.props.hideModal();
  };

  setGeneralLedgerValue = (e, value, setFieldValue, index) => {
    setFieldValue(`selectedGeneralLedgers.${index}.glId`, value);
    setFieldValue(`selectedGeneralLedgers.${index}.glSubId`, "");
    if (this.props.generalLedgers) {
      this.props.generalLedgers.forEach((ledger) => {
        if (ledger.id == value) {
          setFieldValue(`selectedGeneralLedgers.${index}.glDescription`, ledger.description);
        }
      });
    }
    this.setState({ generalLedgerSelected: true });
  };

  setGeneralLedgerLocationValue = (e, value, setFieldValue, index) => {
    setFieldValue(`selectedGeneralLedgers.${index}.glLocationId`, value);
    this.setState({ generalLedgerSelected: true });
  };

  handleRemoveGeneralLedger = (formProps, remove, index, setFieldValue) => {
    const values = { ...formProps.values };
    if (!values.selectedGeneralLedgers) return;

    const toDelete = [...this.state.generalLedgersToDelete];
    const item = values.selectedGeneralLedgers[index];
    if (item?.id) toDelete.push(item.id);

    const updated = values.selectedGeneralLedgers.filter((_, i) => i !== index);
    const next = updated.length === 0
      ? [{ glLocationId: "", glSubId: "", glId: "", glDescription: "" }]
      : updated;

    setFieldValue("selectedGeneralLedgers", next);
    this.setState({
      generalLedgersToDelete: toDelete,
      generalLedgerSelected: next.some((gl) => gl.glLocationId || gl.glId || gl.glSubId),
    });
  };

  buildGeneralLedgerTitle(selectedGeneralLedger) {
    const { systemLocations, generalLedgers, generalLedgerSubAccounts } = this.props;
    let title = "";
    if (selectedGeneralLedger.glLocationId && systemLocations) {
      const loc = systemLocations.find((l) => l.id == selectedGeneralLedger.glLocationId);
      if (loc) title += loc.name;
    }
    if (selectedGeneralLedger.glId && generalLedgers) {
      const gl = generalLedgers.find((g) => g.id == selectedGeneralLedger.glId);
      if (gl) title += (title ? ":" : "") + gl.accountNumber;
    }
    if (selectedGeneralLedger.glSubId && generalLedgerSubAccounts) {
      const sa = generalLedgerSubAccounts.find((s) => s.id == selectedGeneralLedger.glSubId);
      if (sa) title += "." + sa.subAccountNumber;
    }
    return title;
  }

  scrollDropdownIntoView = (e) => {
    const elementClicked = e.target.nodeName;
    const boundingElement = e.currentTarget;
    const modalBody = document.getElementsByClassName("modal-body")[0];
    if (elementClicked !== "LABEL") {
      setTimeout(() => {
        if (boundingElement.getBoundingClientRect().bottom > modalBody.getBoundingClientRect().bottom) {
          if (boundingElement.getBoundingClientRect().height < modalBody.getBoundingClientRect().height) {
            boundingElement.scrollIntoView({ block: "end" });
          }
        }
      }, 200);
    }
  };

  formSubmit = (values, actions) => {
    this.setState({ alertMessage: "" }, () => {
      const postData = {
        name: values.name,
        description: values.description,
        percentage: values.percentage,
        amount: values.amount,
        isActive: values.isActive,
        validDays: values.validDays,
        validMonths: values.validMonths,
        startDate: toDateString(values.startDate),
        endDate: toDateString(values.endDate),
        exceedAmountDue: values.exceedAmountDue,
        service: true,
        autoApply: values.autoApply,
        selectedGeneralLedgers: values.selectedGeneralLedgers,
        generalLedgersToDelete: values.generalLedgersToDelete,
      };

      const { value: discount } = this.props.modalProps;
      const promise = discount?.id
        ? this.props.updateCatalogDiscount(discount.id, postData)
        : this.props.addCatalogDiscount(postData);

      promise.then(
        (response) => {
          if (!response?.data?.success) {
            actions.setSubmitting(false);
            this.setState({ alertMessage: response?.data?.error?.message || "Unknown error." });
            return;
          }
          if (typeof this.props.modalProps.onSuccess === "function")
            this.props.modalProps.onSuccess(response.data?.discount);
          this.hideModal();
        },
        (err) => {
          actions.setSubmitting(false);
          this.setState({
            alertMessage: err?.response?.data?.error?.message || "Unknown error. Please try again later.",
          });
        }
      );
    });
  };

  render() {
    const { alertMessage, isLoading, discountDetail } = this.state;
    const { modalProps, generalLedgers, generalLedgerSubAccounts, systemLocations } = this.props;
    const { value: discountProp } = modalProps;
    const isEdit = !!discountProp?.id;
    const discount = discountDetail || discountProp;

    return (
      <Fragment>
        <div className="modal" style={{ display: "block" }} tabIndex="-1" role="dialog">
          {isLoading && (
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{isEdit ? "Edit Discount" : "Add Discount"}</h5>
                  <button onClick={this.hideModal} type="button" className="close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <Loader />
              </div>
            </div>
          )}
          {!isLoading && <Formik
            key={discount?.id || "new"}
            initialValues={{
              name: (discount?.name || "").toUpperCase(),
              description: discount?.description || "",
              percentage: discount?.percentage != null ? discount.percentage : "00.00",
              amount: discount?.amount != null ? discount.amount : "0.00",
              isActive: discount?.isActive != null ? discount.isActive : true,
              validDays: discount?.validDays != null ? discount.validDays : "0",
              validMonths: discount?.validMonths != null ? discount.validMonths : "0",
              startDate: toDateValue(discount?.startDate),
              endDate: toDateValue(discount?.endDate),
              exceedAmountDue: discount?.exceedAmountDue ?? false,
              retail: discount?.retail ?? false,
              service: true,
              oneTime: discount?.oneTime ?? false,
              autoApply: discount?.autoApply ?? true,
              autoApplyCount: discount?.autoApplyCount ?? "",
              selectedGeneralLedgers: this.state.selectedGeneralLedgers,
              generalLedgersToDelete: this.state.generalLedgersToDelete,
            }}
            validationSchema={this.formSchema()}
            onSubmit={this.formSubmit}
          >
            {({ handleChange, handleSubmit, handleBlur, values, errors, touched, isSubmitting, setFieldValue, ...formProps }) => (
              <form onSubmit={handleSubmit}>
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">{isEdit ? "Edit Discount" : "Add Discount"}</h5>
                      <button onClick={this.hideModal} type="button" className="close">
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="modal-body cmv-form form-horizontal">
                      {!isEmpty(alertMessage) && (
                        <div className="alert alert-inline alert-danger alert-dismissible">
                          <p className="mb-0">{alertMessage}</p>
                        </div>
                      )}

                      {booleanSlider("edit-discount-isactive", "Active", "isActive", values, handleChange, handleBlur)}

                      <div className="form-group">
                        <TextInput
                          label="Name"
                          name="name"
                          value={values.name}
                          onChange={(e) => setFieldValue('name', e.target.value.toUpperCase())}
                          onBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                          maxLength={45}
                        />
                      </div>
                      <div className="form-group">
                        <TextInput
                          label="Description"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                          maxLength={255}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="percentage">Percentage</label>
                        <div className="input-group">
                          <div className="input-group-prepend">
                            <span className="input-group-text">%</span>
                          </div>
                          <input
                            id="percentage"
                            name="percentage"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                            className={errors.percentage && touched.percentage ? 'form-control is-invalid' : 'form-control'}
                            value={values.percentage}
                            onChange={(e) => {
                              const { value } = e.target;
                              if (value === '' || /^\d{0,3}(\.\d{0,2})?$/.test(value)) {
                                setFieldValue('percentage', value);
                              }
                            }}
                            onBlur={handleBlur}
                          />
                        </div>
                        {errors.percentage && touched.percentage &&
                          <div className="invalid-feedback d-block">{errors.percentage}</div>
                        }
                        <small className="form-text text-muted">
                          The Percentage will be applied in billing to the feature price after a flat-rate discount, when present.
                        </small>
                      </div>
                      <div className="form-group">
                        <label htmlFor="amount">Flat-Rate</label>
                        <div className="input-group">
                          <div className="input-group-prepend">
                            <span className="input-group-text">$</span>
                          </div>
                          <input
                            id="amount"
                            name="amount"
                            type="number"
                            step="0.01"
                            min="0"
                            max="9999999999.99"
                            onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                            className={errors.amount && touched.amount ? 'form-control is-invalid' : 'form-control'}
                            value={values.amount}
                            onChange={(e) => {
                              const { value } = e.target;
                              if (value === '' || /^\d{0,10}(\.\d{0,2})?$/.test(value)) {
                                setFieldValue('amount', value);
                              }
                            }}
                            onBlur={handleBlur}
                          />
                        </div>
                        {errors.amount && touched.amount &&
                          <div className="invalid-feedback d-block">{errors.amount}</div>
                        }
                        <small className="form-text text-muted">
                          The Flat-Rate will be applied in billing to the feature price before a percentage discount, when present.
                        </small>
                      </div>
                      <div className="form-group">
                        <TextInput
                          label="Valid Days"
                          name="validDays"
                          type="number"
                          step="1"
                          min="0"
                          max="999"
                          onKeyDown={(e) => (e.key === '-' || e.key === '.') && e.preventDefault()}
                          value={values.validDays}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (value === '' || /^\d{0,3}$/.test(value)) {
                              setFieldValue('validDays', value);
                            }
                          }}
                          onBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                        />
                      </div>
                      <div className="form-group">
                        <TextInput
                          label="Valid Months"
                          name="validMonths"
                          type="number"
                          step="1"
                          min="0"
                          max="999"
                          onKeyDown={(e) => (e.key === '-' || e.key === '.') && e.preventDefault()}
                          value={values.validMonths}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (value === '' || /^\d{0,3}$/.test(value)) {
                              setFieldValue('validMonths', value);
                            }
                          }}
                          onBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="startDate">Start Date</label>
                        <div className="customDatePickerWidth">
                          <DatePicker
                            id="startDate"
                            name="startDate"
                            className={"form-control text-left" + (errors.startDate && touched.startDate ? " is-invalid" : "")}
                            placeholderText="Select Date"
                            autoFocus={false}
                            shouldCloseOnSelect={true}
                            popperPlacement="auto"
                            selected={values.startDate}
                            dateFormat="MM/dd/yyyy"
                            onChange={(date) => setFieldValue('startDate', date)}
                            onBlur={handleBlur}
                            isClearable={true}
                          />
                          {errors.startDate && touched.startDate &&
                            <div className="invalid-feedback d-block">{errors.startDate}</div>
                          }
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="endDate">End Date</label>
                        <div className="customDatePickerWidth">
                          <DatePicker
                            id="endDate"
                            name="endDate"
                            className={"form-control text-left" + (errors.endDate && touched.endDate ? " is-invalid" : "")}
                            placeholderText="Select Date"
                            autoFocus={false}
                            shouldCloseOnSelect={true}
                            popperPlacement="auto"
                            selected={values.endDate}
                            dateFormat="MM/dd/yyyy"
                            onChange={(date) => setFieldValue('endDate', date)}
                            onBlur={handleBlur}
                            isClearable={true}
                          />
                          {errors.endDate && touched.endDate &&
                            <div className="invalid-feedback d-block">{errors.endDate}</div>
                          }
                        </div>
                      </div>
                      <div className="form-check checkbox-slider checkbox-slider--b-flat">
                        <label>
                          <input id="edit-discount-exceedamountdue" type="checkbox" name="exceedAmountDue" checked={values.exceedAmountDue} onChange={handleChange} onBlur={handleBlur} />
                          <span>&nbsp;</span>
                        </label>
                        <label htmlFor="edit-discount-exceedamountdue">Exceed Amount Due</label>
                      </div>
                      <div className="form-check checkbox-slider checkbox-slider--b-flat">
                        <label>
                          <input id="edit-discount-autoapply" type="checkbox" name="autoApply" checked={values.autoApply} onChange={handleChange} onBlur={handleBlur} />
                          <span>&nbsp;</span>
                        </label>
                        <label htmlFor="edit-discount-autoapply">Auto Apply</label>
                      </div>
                      <span>&nbsp;</span>
                      <label>General Ledgers (optional)</label>
                      <FieldArray name="selectedGeneralLedgers" render={({ remove, push }) => (
                        <>
                          {values.selectedGeneralLedgers && values.selectedGeneralLedgers.map((selectedGl, index) => (
                            <div key={index} style={{ paddingBottom: "10px" }}>
                              <div className="card card-sm">
                                <div className="card-header" style={{ paddingRight: "0px" }}>
                                  <div className="col-md-9" style={{ paddingLeft: "0px" }}>
                                    <label style={{ fontSize: "0.95rem" }}>
                                      {(selectedGl.glLocationId || selectedGl.glId || selectedGl.glSubId)
                                        ? this.buildGeneralLedgerTitle(selectedGl)
                                        : "General Ledger"}
                                    </label>
                                  </div>
                                  <div className="col-md-2" style={{ textAlign: "end" }}>
                                    {(values.selectedGeneralLedgers.length > 1 || selectedGl.glLocationId || selectedGl.glId || selectedGl.glSubId) && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => this.handleRemoveGeneralLedger({ values }, remove, index, setFieldValue)}
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="card-body">
                                  <div className="row">
                                    <div className="form-group col-sm-4">
                                      <div onClick={this.scrollDropdownIntoView}>
                                        <FormSelect
                                          fieldName={`selectedGeneralLedgers.${index}.glLocationId`}
                                          placeholder="Select Location"
                                          options={systemLocationsAsOptions(removeSelectedLocations(systemLocations, index, values.selectedGeneralLedgers))}
                                          setFieldValue={(e, value) => this.setGeneralLedgerLocationValue(e, value, setFieldValue, index)}
                                          value={selectedGl.glLocationId}
                                          onBlur={handleBlur}
                                          errors={errors}
                                          touched={touched}
                                        />
                                      </div>
                                    </div>
                                    <div className="form-group col-sm-4">
                                      <div onClick={this.scrollDropdownIntoView}>
                                        <FormSelect
                                          fieldName={`selectedGeneralLedgers.${index}.glId`}
                                          placeholder="Select Account"
                                          options={generalLedgersAsOptions(generalLedgers)}
                                          setFieldValue={(e, value) => this.setGeneralLedgerValue(e, value, setFieldValue, index)}
                                          value={selectedGl.glId}
                                          onBlur={handleBlur}
                                          errors={errors}
                                          touched={touched}
                                        />
                                      </div>
                                      <span className="text-muted">{selectedGl.glDescription}</span>
                                    </div>
                                    <div className="form-group col-sm-4">
                                      <div onClick={this.scrollDropdownIntoView}>
                                        <FormSelect
                                          fieldName={`selectedGeneralLedgers.${index}.glSubId`}
                                          placeholder="Select Sub Account"
                                          options={generalLedgerSubAccountsAsOptions(matchCurrentSelectedGeneralLedger(generalLedgerSubAccounts, selectedGl.glId))}
                                          setFieldValue={setFieldValue}
                                          value={selectedGl.glSubId}
                                          onBlur={handleBlur}
                                          errors={errors}
                                          touched={touched}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          <span>&nbsp;</span>
                          <div className="clearfix">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary float-right"
                              onClick={() => push({ glLocationId: "", glSubId: "", glId: "", glDescription: "" })}
                            >
                              Add Another
                            </button>
                          </div>
                        </>
                      )} />

                    </div>
                    <div className="modal-footer">
                      <button onClick={this.hideModal} className="btn" type="button">
                        Cancel
                      </button>
                      <button disabled={isSubmitting} type="submit" className="btn btn-primary">
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </Formik>}
        </div>
        <div className="modal-backdrop show" tabIndex="1" />
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  modalProps: getModalData(state).modalProps,
  generalLedgers: getAdmin(state).generalLedgers,
  generalLedgerSubAccounts: getAdmin(state).generalLedgerSubAccounts,
  systemLocations: getAdmin(state).systemLocations,
});

const mapDispatchToProps = {
  hideModal,
  addCatalogDiscount,
  updateCatalogDiscount,
  getCatalogDiscount,
  getGeneralLedgers,
  getGeneralLedgerSubAccounts,
  getSystemLocations,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditDiscountModal);